import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryState, parseAsString } from "nuqs";
import { Link } from "@shopmuchmore/design-system";
import {
  ShoppingCart,
  User,
  LogOut,
  ChevronDown,
  Bell,
  Menu,
} from "lucide-react";
import {
  Button,
  SearchInput,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@shopmuchmore/design-system";
import { useAuthStore } from "../../features/auth/auth.store";
import { useCartQuery } from "../../features/cart/cart.services";
import { useUnreadCount } from "../../features/notifications/notifications.store";
import { useNotificationSync } from "../../features/notifications/useNotificationSync";
import { formatPrice } from "@shopmuchmore/common-utils";
import AppLogo from "../AppLogo";
import { NavigationDrawer } from "./NavigationDrawer";

function NotificationIndicator() {
  const notificationCount = useUnreadCount();

  return (
    <Link
      href="/account/notifications"
      variant="ghost"
      className="relative p-2 transition-colors"
      aria-label="Notifications"
    >
      <div className="relative">
        <Bell className="size-5" />
        {notificationCount > 0 && (
          <Badge
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center p-0 text-xs"
          >
            {notificationCount > 99 ? "99+" : notificationCount}
          </Badge>
        )}
      </div>
    </Link>
  );
}

function CartIndicator({ showLabel = true }: { showLabel?: boolean }) {
  const { data: cart } = useCartQuery();
  const itemCount = cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
  const subtotal = Number(cart?.cart_subtotal ?? 0);
  const formattedTotal = formatPrice(subtotal);

  return (
    <Link
      href="/cart"
      variant="ghost"
      className="relative flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors p-2"
    >
      <span className="sr-only">Shopping cart</span>
      <div className="relative">
        <ShoppingCart className="size-5" />
        {itemCount > 0 && (
          <Badge
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center p-0 text-xs"
          >
            {itemCount > 99 ? "99+" : itemCount}
          </Badge>
        )}
      </div>
      {showLabel && <span className="hidden md:inline ml-3">{formattedTotal}</span>}
    </Link>
  );
}

function ProfileDropdown() {
  const [returnUrl] = useQueryState("returnUrl", parseAsString);
  const { customer, logout } = useAuthStore();
  const isAuthenticated = !!customer;

  const authQuery = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "";


  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hidden md:flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors outline-none p-2">
          <User className="size-5" />
          <span className="max-w-[150px] truncate">
            {isAuthenticated ? [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") : "Account"}
          </span>
          <ChevronDown className="size-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56 mt-5">
        {!isAuthenticated && (
          <>
            <div className="p-2 flex flex-col gap-2">
              <Link href={`/auth/signup${authQuery}`}>Sign Up</Link>
              <Link href={`/auth/login${authQuery}`} variant="outline">
                Login
              </Link>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem asChild>
          <Link href="/account" variant="ghost" className="w-full justify-start cursor-pointer h-auto p-2 font-normal" size="sm">
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/account/orders" variant="ghost" className="size-full justify-start cursor-pointer h-auto p-2 font-normal" size="sm">
            My Orders
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/account/wishlist" variant="ghost" className="w-full justify-start cursor-pointer h-auto p-2 font-normal" size="sm">
            Wishlist
          </Link>
        </DropdownMenuItem>

        {isAuthenticated && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-500 focus:text-red-500 cursor-pointer"
              onClick={() => logout()}
            >
              <LogOut className="mr-2 size-4" />
              Log out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}




function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const isBrowsePage = location.pathname === '/browse';

  // Initialize notification syncing
  useNotificationSync();

  // Using nuqs hook when on browse page, otherwise local state
  const [query, setQuery] = useQueryState('search', parseAsString.withDefault(''));
  const [localSearch, setLocalSearch] = useState("");

  // Sync local search when URL query changes
  useEffect(() => {
    if (query) setLocalSearch(query);
  }, [query]);

  const handleSearch = (value: string) => {
    if (isBrowsePage) {
      setQuery(value || null);
    } else {
      navigate(`/browse?search=${encodeURIComponent(value)}`);
    }
  };

  return (
    <header className="bg-background border-b border-border">
      <div className="w-full px-4">
        {/* Main row */}
        <div className="flex items-center justify-between h-14 md:h-16 gap-2 md:gap-8">
          {/* Left: Menu + Logo */}
          <div className="flex items-center gap-1 md:gap-4">
            <NavigationDrawer className="md:hidden p-2 hover:text-primary transition-colors" aria-label="Open menu">
              <Menu className="size-6" />
            </NavigationDrawer>
            <div className="shrink-0 flex items-center gap-4">
              <AppLogo />
            </div>
          </div>

          {/* Search Box - Desktop only */}
          <div className="hidden md:flex flex-1 max-w-2xl">
            <div className="flex w-full">
              <SearchInput
                placeholder="Search products, brands and categories"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onSearch={handleSearch}
                onClear={() => {
                  setLocalSearch("");
                  handleSearch("");
                }}
                className="rounded-r-none border-r-0 focus-visible:ring-0"
              />
              <Button
                onClick={() => handleSearch(localSearch)}
                className="rounded-l-none"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            <NotificationIndicator />
            <CartIndicator showLabel={true} />
            <ProfileDropdown />
          </div>
        </div>

        {/* Search Box - Mobile only */}
        <div className="md:hidden pb-3">
          <div className="flex w-full">
            <SearchInput
              placeholder="Search products, brands and categories"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onSearch={handleSearch}
              onClear={() => {
                setLocalSearch("");
                handleSearch("");
              }}
              className="rounded-r-none border-r-0 focus-visible:ring-0"
            />
            <Button
              onClick={() => handleSearch(localSearch)}
              className="rounded-l-none"
            >
              Search
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopNav;
